/**
 * Shared constants for whole-house canvas rendering.
 * Used by both frontend (PixiJS) and backend (node-canvas) to ensure visual consistency.
 */

/**
 * Available marker shapes.
 * This is the source of truth for marker shape options.
 */
export const MARKER_SHAPES = [
	'circle',
	'square',
	'gps',
	'heart',
	'star',
	'hexagon',
	'triangle',
	'diamond',
	'shield',
	'bookmark',
	'bell',
	'pentagon',
] as const;

/**
 * Marker shape colors.
 * These hex codes are used for rendering marker icons in both frontend and backend.
 */
export const MARKER_COLORS: Record<(typeof MARKER_SHAPES)[number], string> = {
	circle: '#e3538f', // pink
	square: '#ffa722', // orange
	gps: '#b2cf00', // lime
	heart: '#b59855', // primary
	star: '#000000', // black
	hexagon: '#f486a7', // pink-light
	triangle: '#0066cc', // blue
	diamond: '#cc0066', // magenta
	shield: '#009900', // green
	bookmark: '#6600cc', // purple
	bell: '#cc6600', // dark orange
	pentagon: '#006666', // teal
} as const;

/**
 * Per-marker adjustments for size and position in backend PDF rendering.
 * Tuple format: [sizeAdjustment, positionAdjustment]
 * - sizeAdjustment: offset from base targetSize (10px), e.g., -1 = 9px, 0 = 10px
 * - positionAdjustment: subtracted from base iconYOffset (1.5), e.g., 1 = 0.5 offset, 0 = 1.5 offset
 * Markers not listed use default values (size 10, yOffset 1.5).
 */
export const MARKER_ADJUSTMENTS: Partial<Record<(typeof MARKER_SHAPES)[number], [number, number]>> =
	{
		circle: [0, 1],
		square: [0, 1],
		gps: [0, 1],
		heart: [0, 1],
		triangle: [0, 1],
		bookmark: [-1, 1], // flag icon - smaller and raised
	} as const;

/**
 * Marker rendering styles.
 */
export const MARKER_STYLES = {
	/** Marker icon size in pixels */
	size: 40,
	/** Label text styles */
	label: {
		fontSize: 14,
		fontFamily: 'Arial',
		color: '#ffffff',
	},
} as const;

/**
 * Path/line rendering styles.
 */
export const LINE_STYLES = {
	thickness: {
		normal: 3,
	},
	cap: 'round' as CanvasLineCap,
	join: 'round' as CanvasLineJoin,
} as const;

/**
 * Polygon fill rendering styles.
 */
export const POLYGON_STYLES = {
	opacity: {
		normal: 0.2,
	},
} as const;

/**
 * Note rendering styles.
 * Notes are comments with metadata.internal === undefined.
 */
export const NOTE_STYLES = {
	color: '#0066ff', // blue
	fillAlpha: 0.05,
	lineAlpha: 0.8,
	lineWidth: 2,
	dashLength: 8,
	gapLength: 4,
	fontSize: 24,
	fontFamily: 'Arial',
	textOffset: 5, // offset from top-left corner
} as const;

/**
 * Comment rendering styles.
 * Comments are comments with metadata.internal === true.
 */
export const COMMENT_STYLES = {
	color: '#006400', // dark green
	fillAlpha: 0.05,
	lineAlpha: 0.8,
	lineWidth: 2,
	dashLength: 8,
	gapLength: 4,
	fontSize: 18,
	fontFamily: 'Arial',
	textOffset: 5, // offset from top-left corner
} as const;

/**
 * Background dimming effect styles.
 * Used to dim the base image and make drawn layers/notes stand out.
 */
export const BACKGROUND_DIM_STYLES = {
	/** Opacity of the dimming overlay (0-1) */
	alpha: 0.8,
} as const;

/**
 * Background dimming effect for PNG export.
 * Lower value = more lightening (less dark) in final PNG.
 */
export const BACKGROUND_DIM_STYLES_PNG = {
	/** Opacity of the dimming overlay for PNG exports (0-1) */
	alpha: 0.5,
} as const;
